import type { FailureScope, ReplicaFailures } from '@unni-craftsman/turso-fts-common'
import type { WorkerCallError } from './worker-error'
import { deserializeWorkerError } from './worker-error'

type Method = Exclude<keyof ReplicaFailures, 'subscribe' | 'close'>
export type FailureRequest = { [K in Method]: { method: K; args: Parameters<ReplicaFailures[K]> } }[Method]
export type FailureEvent = { id: string; error?: WorkerCallError }

export function executeFailureCommand(failures: ReplicaFailures, request: FailureRequest): Promise<unknown> {
  switch (request.method) {
    case 'version': return failures.version(...request.args)
    case 'record': return failures.record(...request.args)
    case 'resolve': return failures.resolve(...request.args)
    case 'retry': return failures.retry(...request.args)
    case 'discard': return failures.discard(...request.args)
    case 'list': return failures.list(...request.args)
    case 'detail': return failures.detail(...request.args)
  }
  throw new Error('Unknown native failure command')
}

export function createFailureClient(
  call: (request: FailureRequest) => Promise<unknown>,
  subscribe: (scope: FailureScope, id: string) => Promise<void>,
  unsubscribe: (id: string) => Promise<void>,
  events: Set<(event: FailureEvent) => void>,
): ReplicaFailures {
  let closed = false
  const releases = new Map<string, () => void>()
  function invoke<T>(request: FailureRequest): Promise<T> {
    if (closed) return Promise.reject(new Error('Turso failure client closed'))
    return call(request) as Promise<T>
  }
  return {
    version: (...args) => invoke({ method: 'version', args }),
    record: (...args) => invoke({ method: 'record', args }),
    resolve: (...args) => invoke({ method: 'resolve', args }),
    retry: (...args) => invoke({ method: 'retry', args }),
    discard: (...args) => invoke({ method: 'discard', args }),
    list: (...args) => invoke({ method: 'list', args }),
    detail: (...args) => invoke({ method: 'detail', args }),
    async subscribe(scope, listener, onError) {
      if (closed) throw new Error('Turso failure client closed')
      const ownedScope = { ...scope, replica: new Uint8Array(scope.replica) }
      const id = crypto.randomUUID()
      const notify = (event: FailureEvent): void => {
        if (event.id !== id || closed) return
        if (event.error) onError(deserializeWorkerError(event.error))
        else listener()
      }
      events.add(notify)
      const release = (): void => { events.delete(notify); releases.delete(id) }
      releases.set(id, release)
      try { await subscribe(ownedScope, id) }
      catch (error) { release(); throw error }
      if (closed) { await unsubscribe(id); throw new Error('Turso failure client closed') }
      return () => {
        if (!releases.has(id)) return
        release()
        void unsubscribe(id).catch(onError)
      }
    },
    async close() {
      if (closed) return
      closed = true
      const ids = [...releases.keys()]
      for (const release of releases.values()) release()
      await Promise.all(ids.map(id => unsubscribe(id)))
    },
  }
}
